import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Heart, Star, Sparkles } from 'lucide-react';
interface StatusBadgeProps {
  isCorrect: boolean;
  motivationalText: string;
  xpGained: number;
  mode?: 'full' | 'compact';
}
// Radial burst particle positions (8 directions)
const BURST_PARTICLES = [{
  x: 0,
  y: -70
}, {
  x: 49,
  y: -49
}, {
  x: 70,
  y: 0
}, {
  x: 49,
  y: 49
}, {
  x: 0,
  y: 70
}, {
  x: -49,
  y: 49
}, {
  x: -70,
  y: 0
}, {
  x: -49,
  y: -49
}];
const CONFETTI_DOTS = [{
  top: '12%',
  left: '18%',
  color: 'bg-yellow-300',
  size: 'w-2.5 h-2.5',
  delay: 0.3
}, {
  top: '20%',
  right: '14%',
  color: 'bg-white/80',
  size: 'w-2 h-2',
  delay: 0.45
}, {
  top: '65%',
  left: '10%',
  color: 'bg-emerald-200',
  size: 'w-3 h-3',
  delay: 0.5
}, {
  top: '70%',
  right: '12%',
  color: 'bg-yellow-200',
  size: 'w-2 h-2',
  delay: 0.35
}];
/**
 * StatusBadge — celebration-first feedback header.
 * Correct: animated emerald burst with checkmark and 'Goed gedaan!'
 * Wrong: warm orange card with pulsing heart and supportive Dutch copy
 */
export function StatusBadge({
  isCorrect,
  motivationalText,
  xpGained,
  mode = 'full'
}: StatusBadgeProps) {
  if (mode === 'compact') {
    return <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold ${isCorrect ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-4357">
        {isCorrect ? '✓' : '✗'}
      </div>;
  }
  if (isCorrect) {
    return <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 24
    }} className="relative overflow-hidden rounded-3xl p-8 text-center bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-600 shadow-2xl shadow-emerald-300/50 border border-emerald-300/30" data-id="element-4358">
        {/* Confetti dots */}
        {CONFETTI_DOTS.map((dot, i) => <motion.div key={i} className={`absolute rounded-full ${dot.color} ${dot.size} pointer-events-none`} style={{
        top: dot.top,
        left: dot.left,
        right: dot.right
      }} initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: [0, 1, 1, 0],
        scale: [0, 1.2, 1, 0.8],
        y: [0, -8, -14, -20]
      }} transition={{
        delay: dot.delay,
        duration: 1.8,
        ease: 'easeOut'
      }} data-id="element-4359" />)}

        {/* Radial burst + checkmark */}
        <div className="relative flex items-center justify-center mb-6" data-id="element-4360">
          {/* Burst particles */}
          {BURST_PARTICLES.map((pos, i) => <motion.div key={i} className="absolute w-3 h-3 rounded-full bg-emerald-200/70 pointer-events-none" initial={{
          x: 0,
          y: 0,
          scale: 0,
          opacity: 1
        }} animate={{
          x: pos.x,
          y: pos.y,
          scale: 1,
          opacity: 0
        }} transition={{
          delay: 0.15 + i * 0.03,
          duration: 0.6,
          ease: 'easeOut'
        }} data-id="element-4361" />)}

          {/* Checkmark circle */}
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: [0, 1.25, 0.95, 1]
        }} transition={{
          delay: 0.1,
          duration: 0.5,
          times: [0, 0.5, 0.75, 1],
          ease: 'easeOut'
        }} className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-xl border-4 border-emerald-200/50 relative z-10" data-id="element-4362">
            <CheckCircle2 className="w-12 h-12 text-emerald-500" strokeWidth={2.5} data-id="element-4363" />
          </motion.div>
        </div>

        {/* Heading */}
        <motion.h2 initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.25
      }} className="text-4xl font-black text-white mb-2 tracking-tight drop-shadow-sm" data-id="element-4364">
          Goed gedaan! 🎉
        </motion.h2>

        {/* XP badge */}
        <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-5 py-2 rounded-full border border-white/30 shadow-sm" data-id="element-4365">
          <Star className="w-4 h-4 fill-yellow-300 text-yellow-300" data-id="element-4366" />
          <span className="font-black text-white text-base" data-id="element-4367">
            +{xpGained} XP
          </span>
        </motion.div>
      </motion.div>;
  }
  // WRONG state — warm, encouraging, never harsh
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    type: 'spring',
    stiffness: 280,
    damping: 24
  }} className="rounded-3xl p-8 text-center bg-orange-50 border-2 border-orange-200 shadow-lg shadow-orange-100/50" data-id="element-4368">
      {/* Pulsing heart */}
      <motion.div animate={{
      scale: [1, 1.07, 1]
    }} transition={{
      duration: 1.8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5 shadow-md border-2 border-orange-100" data-id="element-4369">
        <Heart className="w-11 h-11 text-orange-400 fill-orange-300" strokeWidth={2} data-id="element-4370" />
      </motion.div>

      <motion.h2 initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="text-3xl font-black text-orange-800 mb-2 tracking-tight" data-id="element-4371">
        {motivationalText}
      </motion.h2>

      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.3
    }} className="text-lg text-orange-600 font-medium mb-4" data-id="element-4372">
        Geen zorgen — hier word je beter van! 💪
      </motion.p>

      {/* XP badge */}
      <motion.div initial={{
      opacity: 0,
      y: 6
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4
    }} className="inline-flex items-center gap-2 bg-orange-100 px-5 py-2 rounded-full border border-orange-200 shadow-sm" data-id="element-4373">
        <Sparkles className="w-4 h-4 fill-orange-400 text-orange-400" data-id="element-4374" />
        <span className="font-black text-orange-700 text-base" data-id="element-4375">
          +{xpGained} XP
        </span>
        <span className="text-xs uppercase tracking-wider font-bold text-orange-500" data-id="element-4376">
          voor de inzet
        </span>
      </motion.div>
    </motion.div>;
}